import {toast} from '@wandb/weave/common/components/elements/Toast';
import {isVersionAlias} from '@wandb/weave/common/util/artifacts';
import * as Panel2 from '@wandb/weave/components/Panel2/panel';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import {
  constBoolean,
  constFunction,
  opArtifactAliasAlias,
  opArtifactAliasArtifact,
  opArtifactAliases,
  opArtifactIsPortfolio,
  opArtifactMembershipArtifactAliases,
  opArtifactMembershipArtifactVersion,
  opArtifactMembershipCollection,
  opArtifactName,
  opArtifactProject,
  opArtifactVersionAliases,
  opArtifactVersionId,
  opDict,
  opEntityName,
  opMap,
  opProjectEntity,
  opProjectName,
} from '@wandb/weave/core';
import {useValue} from '@wandb/weave/react';
import copyToClipboard from 'copy-to-clipboard';
import * as React from 'react';
import {useMemo} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import styled from 'styled-components';

import {AddAliasButton, Alias} from '../../../components/ArtifactAlias';
import * as Generated from '../../../generated/graphql';
import {WorkflowsAdminType} from '../../../generated/graphql';
import {Analytics} from '../../../services/analytics';
import {useTeamMember} from '../../../state/graphql/teamMembers';
import {useViewer} from '../../../state/viewer/hooks';
import {propagateErrorsContext} from '../../../util/errors';
import {useIsProjectReadOnly} from '../../Registries/common/hooks';
import {checkRegistryProject} from '../../Registries/common/utils';
import {
  getIsViewerRegistryAdmin,
  getRegistryMembers,
  getRegistryMembersMap,
  getRegistryTeamMembers,
} from '../../Registries/RegistrySettings/RegistryMembersTable/utils';
import {sortAliases} from '../artifact/utils';

const inputType = 'artifactMembership' as const;
type ConfigType = {};

const ArtifactAliasList = styled.div`
  display: flex;
  flex-wrap: wrap;
  row-gap: 5px;
`;

export const PanelArtifactMembershipOverviewAliasList: React.FC<
  Panel2.PanelProps<typeof inputType, ConfigType>
> = props => {
  const artifactMembershipNode = props.input;
  const viewer = useViewer();
  const [addAliases] = Generated.useAddAliasesMutation({
    context: propagateErrorsContext(),
  });
  const [deleteAliases] = Generated.useDeleteAliasesMutation({
    context: propagateErrorsContext(),
  });
  const overviewValue = useValue(
    React.useMemo(() => {
      const artifactNode = opArtifactMembershipCollection({
        artifactMembership: artifactMembershipNode,
      });
      const artifactVersionNode = opArtifactMembershipArtifactVersion({
        artifactMembership: artifactMembershipNode,
      });
      const allAliases = opArtifactVersionAliases({
        artifactVersion: artifactVersionNode,
        hideVersions: constBoolean(true),
      });
      return opDict({
        artifactName: opArtifactName({
          artifact: artifactNode,
        }),
        projectName: opProjectName({
          project: opArtifactProject({
            artifact: artifactNode,
          }),
        }),
        entityName: opEntityName({
          entity: opProjectEntity({
            project: opArtifactProject({
              artifact: artifactNode,
            }),
          }),
        }),
        artifactAliases: opMap({
          arr: opArtifactAliases({
            artifact: artifactNode,
          }),
          mapFn: aliasMapFn,
        }),
        artifactVersionId: opArtifactVersionId({
          artifactVersion: artifactVersionNode,
        }),
        currentAliases: opMap({
          arr: opArtifactMembershipArtifactAliases({
            artifactMembership: artifactMembershipNode,
          }),
          mapFn: aliasMapFn,
        }),
        allAliases: opMap({
          arr: allAliases,
          mapFn: aliasMapFn,
        }),
        isPortfolio: opArtifactIsPortfolio({artifact: artifactNode}),
      } as any);
    }, [artifactMembershipNode])
  );

  const {isProjectReadOnly: readOnly, loading: isProjectReadOnlyLoading} =
    useIsProjectReadOnly(
      overviewValue?.result?.entityName ?? '',
      overviewValue?.result?.projectName ?? ''
    );
  const {viewerIsTeamAdmin} = useTeamMember({
    viewer,
    entityName: overviewValue.result?.entityName,
  });

  const isRegistryProject = useMemo(() => {
    const {isRegistryProject} = checkRegistryProject(
      overviewValue.result?.projectName ?? ''
    );
    return isRegistryProject;
  }, [overviewValue.result?.projectName]);

  const legacyProtectedAliasesQuery = Generated.useEntityProtectedAliasesQuery({
    skip: overviewValue.result?.entityName == null,
    variables: {
      entityName: overviewValue.result?.entityName,
      workflowsAdminType: WorkflowsAdminType.Model,
    },
  });

  const registryProtectedAliasesQuery =
    Generated.useProjectProtectedAliasesQuery({
      skip:
        overviewValue.result?.entityName == null ||
        overviewValue.result?.projectName == null ||
        !isRegistryProject,
      variables: {
        entityName: overviewValue.result?.entityName,
        projectName: overviewValue.result?.projectName,
      },
    });

  const registryProjectMembersQuery = Generated.useRegistryProjectMembersQuery({
    skip:
      overviewValue.result?.entityName == null ||
      overviewValue.result?.projectName == null ||
      !isRegistryProject,
    variables: {
      entityName: overviewValue.result?.entityName,
      projectName: overviewValue.result?.projectName,
    },
  });

  const protectedAliases = React.useMemo(() => {
    const legacyProtectedAliases =
      legacyProtectedAliasesQuery.data?.entity?.protectedAliases ?? [];
    const registryProtectedAliases =
      registryProtectedAliasesQuery.data?.project?.protectedArtifactAliases.map(
        alias => alias.alias
      ) ?? [];
    return isRegistryProject
      ? registryProtectedAliases
      : legacyProtectedAliases;
  }, [
    isRegistryProject,
    legacyProtectedAliasesQuery.data,
    registryProtectedAliasesQuery.data,
  ]);

  const workflowsAdmins = React.useMemo(() => {
    return legacyProtectedAliasesQuery.data?.entity?.workflowsAdmins ?? [];
  }, [legacyProtectedAliasesQuery.data]);

  const isViewerRegistryAdmin = useMemo(() => {
    const registryTeamMembers = getRegistryTeamMembers(
      registryProjectMembersQuery.data?.project?.teamMembers ?? []
    );
    // HACK: we don't need to get the members org role to determine if they are
    // a registry admin, so we just pass in an empty array for the organization
    // members
    const registryMembers = getRegistryMembers(
      [],
      registryProjectMembersQuery.data?.project?.members ?? []
    );
    return getIsViewerRegistryAdmin(
      getRegistryMembersMap(registryMembers),
      getRegistryMembersMap(registryTeamMembers),
      viewer
    );
  }, [
    registryProjectMembersQuery.data?.project?.members,
    registryProjectMembersQuery.data?.project?.teamMembers,
    viewer,
  ]);

  const username = viewer?.username ?? '';
  const isRegistryAdmin = isRegistryProject
    ? isViewerRegistryAdmin
    : workflowsAdmins.some((admin: any) => admin.username === username) ||
      viewerIsTeamAdmin;

  const deleteAlias = React.useCallback(
    async (alias: string) => {
      if (isVersionAlias({alias})) {
        return null;
      }
      if (protectedAliases.includes(alias) && !isRegistryAdmin) {
        toast('This alias is protected. Only registry admins can delete it.');
        return;
      }
      try {
        await deleteAliases({
          variables: {
            artifactID: overviewValue.result.artifactVersionId,
            aliases: [
              {
                alias,
                entityName: overviewValue.result.entityName,
                projectName: overviewValue.result.projectName,
                artifactCollectionName: overviewValue.result.artifactName,
              },
            ],
          },
        });
        await overviewValue.refresh();
      } catch (error) {
        console.error(error);
        toast('Something went wrong when trying to delete this alias.');
      }
      return;
    },
    [deleteAliases, overviewValue, isRegistryAdmin, protectedAliases]
  );

  const createAlias = React.useCallback(
    async (alias: string) => {
      if (
        isVersionAlias({alias}) ||
        overviewValue.result.currentAliases.find((a: any) => a.alias === alias)
      ) {
        return;
      }
      if (protectedAliases.includes(alias) && !isRegistryAdmin) {
        toast('This alias is protected. Only registry admins can add it.');
        return;
      }
      try {
        await addAliases({
          variables: {
            artifactID: overviewValue.result.artifactVersionId,
            aliases: [
              {
                alias,
                entityName: overviewValue.result.entityName,
                projectName: overviewValue.result.projectName,
                artifactCollectionName: overviewValue.result.artifactName,
              },
            ],
          },
        });
        Analytics.track('Artifact interaction', {
          entityName: overviewValue.result.entityName,
          projectName: overviewValue.result.projectName,
          artifactCollectionName: overviewValue.result.artifactName,
          artifactType: overviewValue.result.artifactTypeName,
          eventType: 'create_alias',
          artifactCollectionType: overviewValue.result.isPortfolio
            ? 'portfolio'
            : 'sequence',
        });
        await overviewValue.refresh();
      } catch (error) {
        console.error(error);
        toast('Something went wrong when trying to add this alias.');
      }
    },
    [addAliases, overviewValue, isRegistryAdmin, protectedAliases]
  );

  const copyAlias = React.useCallback(
    (alias: string) => {
      if (overviewValue.loading) {
        return;
      }
      const copyText = `${overviewValue.result.artifactName}:${alias}`;
      copyToClipboard(copyText);
      toast(`Copied alias '${copyText}' to clipboard`);
    },
    [overviewValue]
  );

  const availableAliases = React.useMemo(() => {
    if (overviewValue.loading || overviewValue.result == null) {
      return [];
    }
    // only show protected aliases to registry admins
    const availableProtectedAliases =
      isViewerRegistryAdmin && protectedAliases.length > 0
        ? protectedAliases.map(alias => ({
            alias,
            isProtected: true,
          }))
        : [];
    const existingAliases = overviewValue.result.artifactAliases
      .filter(
        (alias: Pick<Generated.ArtifactAlias, 'alias'>) =>
          !availableProtectedAliases.some(a => a.alias === alias.alias)
      )
      .map((alias: Pick<Generated.ArtifactAlias, 'alias'>) => ({
        alias: alias.alias,
        isProtected: false,
      }));
    return existingAliases.concat(availableProtectedAliases);
  }, [overviewValue, protectedAliases, isViewerRegistryAdmin]);

  if (
    overviewValue.loading ||
    overviewValue.result == null ||
    isProjectReadOnlyLoading
  ) {
    return <></>;
  }

  return (
    <ArtifactAliasList>
      {overviewValue.result.currentAliases
        .sort((a: {alias: string}, b: {alias: string}) =>
          sortAliases(
            {
              name: a.alias,
              isProtectedAlias: protectedAliases.includes(a.alias),
            },
            {
              name: b.alias,
              isProtectedAlias: protectedAliases.includes(b.alias),
            }
          )
        )
        .map(({alias}: {alias: string}) => {
          const isProtected = protectedAliases.includes(alias);
          if (isProtected && overviewValue.result.isPortfolio) {
            return (
              <Tooltip
                key={`${overviewValue.result.artifactName}-${alias}`}
                position={'top center'}
                content={'This alias is protected'}
                trigger={
                  <Alias
                    key={overviewValue.result.artifactName + alias}
                    alias={{alias}}
                    onDelete={
                      !isRegistryAdmin ? undefined : () => deleteAlias(alias)
                    }
                    onClick={() => copyAlias(alias)}
                    isProtected
                  />
                }
              />
            );
          }
          const isSystem = alias === 'latest' || isVersionAlias({alias});
          return (
            <Alias
              key={overviewValue.result.artifactName + alias}
              alias={{alias}}
              onDelete={isSystem ? undefined : () => deleteAlias(alias)}
              onClick={() => copyAlias(alias)}
            />
          );
        })}
      {!readOnly && (
        <AddAliasButton
          aliases={overviewValue.result.currentAliases}
          availableAliases={availableAliases.sort((a: any, b: any) =>
            sortAliases(
              {name: a.alias, isProtectedAlias: a.isProtected},
              {name: b.alias, isProtectedAlias: b.isProtected}
            )
          )}
          onCreate={createAlias}
        />
      )}
    </ArtifactAliasList>
  );
};
const aliasMapFn = constFunction({row: 'artifactAlias'}, ({row}) => {
  return opDict({
    alias: opArtifactAliasAlias({artifactAlias: row}),
    artifactCollectionName: opArtifactName({
      artifact: opArtifactAliasArtifact({artifactAlias: row}),
    }),
  } as any);
});
